import { createClient } from '@connectrpc/connect';
import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { VideoService } from '@buf/motto_cda.bufbuild_es/motto/cda/streaming/video/v1/service_pb';
import {
  ErrorCodeSchema,
  PlaylistFormatSchema,
  type Video,
  type Playlist as PlaylistMessage,
  type DRM as DRMMessage,
} from '@buf/motto_cda.bufbuild_es/motto/cda/streaming/video/v1/video_pb';
import type { DescEnum } from '@bufbuild/protobuf';
import { getRecentPurchaseTime } from '../utils/recentPurchase';
import { cdaTransport, authHeaders } from './transport';

export interface ConcurrencySession {
  /** Signed JWT token for WebSocket authentication */
  token: string;
  /** WebSocket URL to connect to */
  wsUrl: string;
  /** Maximum allowed concurrent streams (for UI display) */
  limit: number;
}

/** Connection state for concurrency session */
export enum ConcurrencyConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
}

/** Messages FROM Server TO Client */
export type ConcurrencyServerMessage =
  | {
      type: 'session_started';
      sessionId: string;
      activeSessions: number;
      limit: number;
      kickedSessions: number;
    }
  | {
      type: 'heartbeat_ack';
      timestamp: number;
      activeSessions: number;
      limit: number;
    }
  | {
      type: 'session_count_changed';
      activeSessions: number;
      limit: number;
    }
  | {
      type: 'kicked';
      reason: 'new_session_started' | 'admin_action' | 'session_replaced';
      message: string;
    }
  | {
      type: 'status';
      activeSessions: number;
      limit: number;
      sessions: Array<{
        sessionId: string;
        deviceId: string;
        videoId: string;
        connectedAt: number;
        isCurrentSession: boolean;
      }>;
    }
  | {
      type: 'error';
      message: string;
    };

/** Messages FROM Client TO Server */
export type ConcurrencyClientMessage =
  | { type: 'heartbeat' }
  | { type: 'get_status' };

export interface VideoData {
  id: string;
  name?: string;
  description?: string;
  playlists?: Playlist[];
  error?: string;
  ad?: {
    adTagUrl?: string;
  };
  concurrencySession?: ConcurrencySession;
}

export interface DVRSettings {
  window_size_seconds: number;
}

export interface Widevine {
  playlistUrl: string;
  licenseUrl: string;
}

export interface Fairplay {
  certificateUrl: string;
  licenseUrl: string;
  playlistUrl: string;
}

export interface Playready {
  playlistUrl: string;
  licenseUrl: string;
}

export interface DRM {
  token?: string;
  licenseCacheKey?: string;
  widevine?: Widevine;
  fairplay?: Fairplay;
  playready?: Playready;
}

export interface Playlist {
  id: string;
  url: string;
  format: string;
  dvr_settings?: DVRSettings;
  drm: DRM;
};

const videoClient = createClient(VideoService, cdaTransport);

// Proto enum number -> proto value name (e.g. 1 -> "ERROR_CODE_GEO_BLOCKED"),
// matching what the previous JSON responses contained and what errorTypes /
// findHLSPlaylist compare against.
const enumName = (desc: DescEnum, value: number): string | undefined =>
  desc.values.find((v) => v.number === value)?.name;

const toDRM = (drm?: DRMMessage): DRM => ({
  token: drm?.token || undefined,
  licenseCacheKey: drm?.licenseCacheKey || undefined,
  widevine: drm?.widevine
    ? { playlistUrl: drm.widevine.playlistUrl, licenseUrl: drm.widevine.licenseUrl }
    : undefined,
  fairplay: drm?.fairplay
    ? {
        playlistUrl: drm.fairplay.playlistUrl,
        licenseUrl: drm.fairplay.licenseUrl,
        certificateUrl: drm.fairplay.certificateUrl,
      }
    : undefined,
  playready: drm?.playready
    ? { playlistUrl: drm.playready.playlistUrl, licenseUrl: drm.playready.licenseUrl }
    : undefined,
});

const toPlaylist = (playlist: PlaylistMessage): Playlist => ({
  id: playlist.id,
  url: playlist.url,
  format: enumName(PlaylistFormatSchema, playlist.format) ?? '',
  dvr_settings: playlist.dvrSettings
    ? { window_size_seconds: playlist.dvrSettings.windowSizeSeconds }
    : undefined,
  drm: toDRM(playlist.drm),
});

const toVideoData = (video: Video): VideoData => ({
  id: video.id,
  name: video.name || undefined,
  playlists: video.playlists.map(toPlaylist),
  error: video.error ? enumName(ErrorCodeSchema, video.error) : undefined,
  ad: video.ad ? { adTagUrl: video.ad.adTagUrl || undefined } : undefined,
  concurrencySession: video.concurrencySession
    ? {
        token: video.concurrencySession.token,
        wsUrl: video.concurrencySession.wsUrl,
        limit: video.concurrencySession.limit,
      }
    : undefined,
});

/**
 * Fetches video data from the Motto Streaming API
 * @param videoId - The ID of the video to fetch
 * @param publicKey - The public key for authentication
 * @param mottoToken - Optional motto token for authenticated requests
 * @param adsEnabled - Optional flag to enable ads
 * @param locale - Optional locale for localization
 * @param deviceId - Optional device ID for concurrency session tracking
 * @returns Promise<VideoData> - The video data
 */
export const fetchVideoData = async (
  videoId: string,
  publicKey: string,
  mottoToken?: string,
  adsEnabled: boolean = false,
  locale: string = 'en',
  deviceId?: string
): Promise<VideoData> => {
  const recentPurchaseTime = getRecentPurchaseTime();

  const response = await videoClient.getVideo(
    {
      videoId,
      enableAds: adsEnabled,
      locale,
      deviceId: deviceId ?? '',
      ...(recentPurchaseTime
        ? { recentPurchaseTime: timestampFromDate(new Date(recentPurchaseTime)) }
        : {}),
    },
    { headers: authHeaders(publicKey, mottoToken) }
  );

  if (!response.video) {
    throw new Error('Failed to fetch video: empty response');
  }
  return toVideoData(response.video);
};

/**
 * Fetches multiple videos data from the Motto Streaming API
 * @param publicKey - The public key for authentication
 * @param videoIds - Array of video IDs to fetch
 * @param mottoToken - Optional motto token for authenticated requests
 * @param skip - Number of videos to skip
 * @param limit - Maximum number of videos to return
 * @param adsEnabled - Optional flag to enable ads
 * @param locale - Optional locale for localization
 * @param deviceId - Optional device ID for concurrency session tracking
 * @returns Promise<VideoData[]> - Array of video data
 */
export async function fetchVideosList(
  publicKey: string,
  videoIds: string[],
  mottoToken?: string,
  skip: number = 0,
  limit: number = 0,
  adsEnabled: boolean = true,
  locale: string = 'en',
  deviceId?: string
): Promise<VideoData[]> {
  if (!videoIds || videoIds.length === 0) {
    return [];
  }

  const recentPurchaseTime = getRecentPurchaseTime();

  const response = await videoClient.batchGetVideos(
    {
      videoIds,
      enableAds: adsEnabled,
      locale,
      deviceId: deviceId ?? '',
      ...(recentPurchaseTime
        ? { recentPurchaseTime: timestampFromDate(new Date(recentPurchaseTime)) }
        : {}),
    },
    { headers: authHeaders(publicKey, mottoToken) }
  );

  return response.videos.map(toVideoData);
}